<template>
  <div class="companies-tab">
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
      <a-button type="primary" icon="plus" @click="openModal(null)">
        {{ $t("action.ajouter") }}
      </a-button>
    </div>

    <a-table
      :loading="loading"
      row-key="_id"
      :columns="columns"
      :data-source="rows"
      :pagination="{ pageSize: 10 }"
      :scroll="{ x: 'max-content' }"
    >
      <template slot="description" slot-scope="text">
        <span>{{ text || "—" }}</span>
      </template>
      <template slot="accountingClass" slot-scope="text, record">
        <a-tag v-if="record.accountingClass" color="geekblue">
          {{ record.accountingClass }}
        </a-tag>
        <a-button
          v-else
          size="small"
          type="link"
          @click="openAssignModal(record)"
        >
          {{ $t("comptabilite.accountingClassAssign") }}
        </a-button>
      </template>
      <template slot="action" slot-scope="text, record">
        <div class="editable-row-operations">
          <a-button type="primary" size="small" @click="openModal(record)">
            {{ $t("action.modifier") }}
          </a-button>
          <a-button type="default" size="small" class="ml-2">
            <a-popconfirm
              :title="$t('comptabilite.companyArchiveConfirm')"
              @confirm="() => archive(record._id)"
            >
              <a>{{ $t("fournisseur.archiver") }}</a>
            </a-popconfirm>
          </a-button>
        </div>
      </template>
    </a-table>

    <a-modal
      :visible="modalVisible"
      :title="editingId ? $t('action.modifier') : $t('action.ajouter')"
      :confirm-loading="saving"
      :width="640"
      @ok="onModalOk"
      @cancel="closeModal"
      destroy-on-close
      wrap-class-name="parametrage-compact-modal"
    >
      <a-form :form="form" layout="vertical" class="compact-param-form">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.companyLegalName')">
              <a-input
                v-decorator="[
                  'name',
                  { rules: [{ required: true, message: $t('requis.nom') }] },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.companyCode')">
              <a-input v-decorator="['code', { initialValue: '' }]" />
            </a-form-item>
          </div>
        </div>
        <div v-if="!editingId" class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('comptabilite.accountingClassReference')">
              <plan-comptable-select
                v-decorator="['accountingParentClass']"
              />
              <div class="text-muted small">
                {{ $t("comptabilite.accountingClassChildHint") }}
              </div>
            </a-form-item>
          </div>
        </div>
        <div v-else class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingClass')">
              <a-input :value="currentChildClass" disabled />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingParentClass')">
              <a-input :value="currentParentClass" disabled />
            </a-form-item>
          </div>
        </div>
        <a-form-item :label="$t('comptabilite.description')">
          <a-textarea v-decorator="['description', { initialValue: '' }]" :rows="2" />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      :visible="assignVisible"
      :title="$t('comptabilite.accountingClassAssign')"
      :confirm-loading="assignSaving"
      :width="520"
      @ok="onAssignOk"
      @cancel="closeAssignModal"
      destroy-on-close
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('comptabilite.accountingClassReference')">
          <plan-comptable-select v-model="assignClass" />
          <div class="text-muted small">
            {{ $t("comptabilite.accountingClassChildHint") }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import PlanComptableSelect from "@/views/compV3/decaissement/components/PlanComptableSelect.vue";

export default {
  name: "CompaniesTab",
  components: { PlanComptableSelect },
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      loading: false,
      rows: [],
      modalVisible: false,
      saving: false,
      editingId: null,
      currentChildClass: "",
      currentParentClass: "",
      assignVisible: false,
      assignSaving: false,
      assignTarget: null,
      assignClass: "",
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("comptabilite.companyLegalName"),
          dataIndex: "name",
          key: "name",
          sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("comptabilite.companyCode"),
          dataIndex: "code",
          key: "code",
        },
        {
          title: this.$t("comptabilite.accountingClass"),
          dataIndex: "accountingClass",
          key: "accountingClass",
          scopedSlots: { customRender: "accountingClass" },
        },
        {
          title: this.$t("comptabilite.description"),
          dataIndex: "description",
          key: "description",
          ellipsis: true,
          scopedSlots: { customRender: "description" },
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          fixed: "right",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  created() {
    this.$gtag.pageview({
      page_path: this.$route.path,
      page_location: window.location.href,
      page_title: document.title + " - Companies",
    });
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await apiClient.post("/companies/filter", {
          query: { status: "active" },
        });
        const list = res.data || [];
        this.rows = list.map((elem) => ({ ...elem, key: elem._id }));
      } catch (e) {
        this.$message.error(this.$t("warning.connectionProblem"));
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    openModal(record) {
      this.editingId = record ? record._id : null;
      this.currentChildClass = (record && record.accountingClass) || "—";
      this.currentParentClass = (record && record.accountingParentClass) || "—";
      this.modalVisible = true;
      this.$nextTick(() => {
        if (record) {
          this.form.setFieldsValue({
            name: record.name,
            code: record.code || "",
            description: record.description || "",
          });
        } else {
          this.form.resetFields();
        }
      });
    },
    closeModal() {
      this.modalVisible = false;
      this.editingId = null;
      this.currentChildClass = "";
      this.currentParentClass = "";
      this.form.resetFields();
    },
    onModalOk(e) {
      e.preventDefault();
      this.submit();
    },
    submit() {
      this.form.validateFields((err, values) => {
        if (err) return;
        this.saving = true;
        const data = { ...values };
        if (this.editingId) {
          delete data.accountingParentClass;
        } else if (!data.accountingParentClass) {
          delete data.accountingParentClass;
        }
        const payload = { data };
        const req = this.editingId
          ? apiClient.patch(`/companies/${this.editingId}`, payload)
          : apiClient.put("/companies", payload);
        req
          .then(() => {
            this.$message.success(this.$t("success.operationEffectue"));
            this.closeModal();
            this.load();
          })
          .catch((err) => showApiError(this, err))
          .finally(() => {
            this.saving = false;
          });
      });
    },
    archive(id) {
      apiClient
        .patch(`/companies/${id}`, { data: { status: "inactive" } })
        .then(() => {
          this.$message.success(this.$t("comptabilite.companyArchived"));
          this.rows = this.rows.filter((item) => item._id !== id);
        })
        .catch((err) => showApiError(this, err));
    },
    openAssignModal(record) {
      this.assignTarget = record;
      this.assignClass = "";
      this.assignVisible = true;
    },
    closeAssignModal() {
      this.assignVisible = false;
      this.assignTarget = null;
      this.assignClass = "";
    },
    onAssignOk() {
      if (!this.assignTarget || !this.assignClass) {
        this.$message.warning(this.$t("comptabilite.accountingClassParentMissingShort"));
        return;
      }
      this.assignSaving = true;
      apiClient
        .patch(`/companies/${this.assignTarget._id}/assign-class`, {
          data: { accountingParentClass: this.assignClass },
        })
        .then(() => {
          this.$message.success(this.$t("success.operationEffectue"));
          this.closeAssignModal();
          this.load();
        })
        .catch((err) => showApiError(this, err))
        .finally(() => {
          this.assignSaving = false;
        });
    },
  },
};
</script>

<style scoped>
.compact-param-form >>> .ant-form-item {
  margin-bottom: 10px;
}
</style>
